import React, { createContext, useContext, useState, ReactNode, useEffect, useMemo, useCallback, useRef } from 'react';
import { TaskListItem } from './models/task';
import { RoleDTO } from './models/role';
import timerService from './services/timerService';
import authService from './services/authService';
import { getModulesNeedingRefresh } from './services/moduleService';
import { Module } from './models/module';

type AppContextType = {
  user: any | null;
  setUser: (user: string | null) => void;
  featurePermissions: any[] | null;
  userTasks: TaskListItem[];
  setUserTasks: (tasks: TaskListItem[]) => void;
  getFeaturePermissionsByModuleID: (moduleID: number) => any[]
  hasTenderingPageAccess: (tenderingModuleID: number) => boolean
};

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<any | null>(null);
  const [userTasks, setUserTasks] = useState<TaskListItem[]>([]);

  // Derived directly from user — no separate state, no extra render
  const featurePermissions: any[] | null = user?.featurePermissions ?? null;

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
      timerService.startSession();
    } else {
      localStorage.removeItem('user');
      timerService.stopSession();
    }
  }, [user]);


  const lastCheckedRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!user) return;

    lastCheckedRef.current = Date.now();

    const moduleIDs: number[] = user.modules?.filter((m: Module) => m.moduleType == 'Tendering').map((m: Module) => m.moduleID) ?? [];
    if (moduleIDs.length === 0) return;

    const interval = setInterval(async () => {
      try {
        const sinceUnixMs = lastCheckedRef.current;
        lastCheckedRef.current = Date.now();

        const { flaggedModuleIDs } = await getModulesNeedingRefresh(moduleIDs, sinceUnixMs);
        if (flaggedModuleIDs.length > 0) {
          const res = await authService.refresh();
          setUser(res.data);
        }
      } catch {
        // do nothing
      }
    }, 30_000);

    return () => clearInterval(interval);
  }, [user?.user?.userID]);

  const getFeaturePermissionsByModuleID = useCallback((moduleID: number): any[] => {
    if(user){
      if (!featurePermissions) return [];
      if(user.roles.some((r: RoleDTO) => r.roleType == "General")){
        return featurePermissions
      }
      else{
        return featurePermissions.filter(fp => fp.moduleID === moduleID);
      }
    }
    else{
      return [];
    }
  }, [user, featurePermissions]);

  // Suppliers (e.g. Foerestandare) only ever get 'Tendering page' scoped to their own Tender's moduleID,
  // never the parent Tendering's moduleID - so an exact-moduleID match never succeeds for them. Fall back
  // to checking if any of their permitted Tender modules rolls up to this tendering via modulePath.
  const hasTenderingPageAccess = useCallback((tenderingModuleID: number): boolean => {
    if (!user || !featurePermissions) return false;
    if (user.roles.some((r: RoleDTO) => r.roleType == "General")) return true;
    if (featurePermissions.some(fp => fp.action === 'Tendering page' && fp.moduleID === tenderingModuleID)) return true;

    return featurePermissions.some(fp => {
      if (fp.action !== 'Tendering page') return false;
      const mod = user.modules?.find((m: Module) => m.moduleID === fp.moduleID);
      if (!mod?.modulePath) return false;
      return Number(mod.modulePath.split('_')[2]) === tenderingModuleID;
    });
  }, [user, featurePermissions]);

  const contextValue = useMemo(() => ({
    user, setUser, featurePermissions, userTasks, setUserTasks, getFeaturePermissionsByModuleID, hasTenderingPageAccess
  }), [user, featurePermissions, userTasks, setUserTasks, getFeaturePermissionsByModuleID, hasTenderingPageAccess]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
